import React, {useRef, useState} from 'react';
import moment from 'moment';
import generateRandomID from 'web/utils/generateRandomID';
import enumList from 'web/utils/enumList';

/**
 * @module useCalendar
 * @description Calendar hook
 * @version 1.0.0
 */
export default function useCalendar(handleSelectSlot, onUpdateDispatch) {
  const [summaryEvents, setSummaryEvents] = useState([]);
  const [currentView, setCurrentView] = useState('month');
  const [events, setEvents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [dragItem, setDragItem] = useState(undefined);

  const generateFormattedEvent = (list) => {
    return list.flatMap((assignment, index) => {
      return assignment.employees.flatMap((employee, index) => {
        const employeeName = employee?.employeeName;
        const uniqueKey = generateRandomID() + assignment.machineSerialNumber;
        const caseNumber = Array.isArray(assignment.cases)
          ? assignment.cases.map((e) => e._caseNumber).join(', ')
          : assignment.cases?._caseNumber || '未知案件';

        const start =
          employee.workStartTime !== 'Invalid date'
            ? moment(employee.workStartTime)
            : moment(assignment.workStartTime);

        const end = employee.workEndTime
          ? moment(assignment.workEndTime)
          : assignment.workEndTime
          ? moment(assignment.workEndTime)
          : null;

        if (!start || !end) return [];

        return [
          {
            id: `event_${uniqueKey}`,
            assignmentId: assignment.assignmentId,
            employeeCode: employee.employeeCode,
            group: employee.employeeCode,
            resourceId: employeeName,
            title: `${employeeName} - ${assignment.machineSerialNumber} - ${caseNumber}`,
            start_time: start,
            end_time: end,
            start: start.toDate(),
            end: end.toDate(),
            eventData: assignment,
          },
        ];
      });
    });
  };

  const generateSummaryEvents = (events, config) => {
    const dailyEventCounts = new Map();
    const seenIds = new Set();
    const uniqueEvents = events.filter((event) => {
      if (seenIds.has(event.assignmentId)) return false;
      seenIds.add(event.assignmentId);
      return true;
    });

    uniqueEvents.forEach((event) => {
      const dayStart = moment(event.start).startOf(config);
      const dayKey = dayStart.valueOf() + dayStart[config]();

      const dayEnd = moment(dayStart).endOf(config);

      if (dailyEventCounts.has(dayKey)) {
        dailyEventCounts.get(dayKey).title += 1;
      } else {
        const dailySummary = {
          title: 1,
          start: dayStart.toDate(),
          end: dayEnd.toDate(),
        };
        dailyEventCounts.set(dayKey, dailySummary);
      }
    });
    return [...dailyEventCounts.values()];
  };

  const handleItemResize = (itemId, endTimeOrStartTime, edge) => {
    const item = items.find((item) => item.id === itemId);
    const formattedTime = moment(endTimeOrStartTime).format(
      'YYYY-MM-DD HH:mm:ss',
    );
    const isNotFinished =
      item.eventData.flgStatus !== enumList.assignmentFlgStatus.FINISH.value;
    const eventData = {
      ...item.eventData,
      workStartTime:
        edge === 'left' && isNotFinished
          ? formattedTime
          : item.eventData.workStartTime,
      workEndTime:
        edge === 'left' && isNotFinished
          ? formattedTime
          : item.eventData.workEndTime,
    };
    const updatedItem = {...item, eventData};

    onUpdateDispatch(updatedItem);
    setDragItem(undefined);
  };

  const handleItemMove = (itemId, dragTime, newGroupOrder) => {
    const item = checkEmployee(itemId, newGroupOrder);
    const isNotFinished =
      item.eventData.flgStatus === enumList.assignmentFlgStatus.FINISH.value;
    const formattedTime = isNotFinished
      ? item.eventData.workStartTime
      : moment(dragTime).format('YYYY-MM-DD HH:mm:ss');
    const updatedItem = {
      ...item,
      eventData: {
        ...item.eventData,
        workStartTime: formattedTime,
      },
    };
    console.log(updatedItem);

    onUpdateDispatch(updatedItem);
    setDragItem(undefined);
  };

  const checkEmployee = (itemId, newGroupOrder) => {
    const item = items.find((item) => item.id === itemId);
    const employeeName = groups[newGroupOrder].id;

    if (
      item.group !== employeeName &&
      item.eventData.flgStatus !== enumList.assignmentFlgStatus.FINISH.value
    ) {
      const newEmployee = groups.find((g) => g.id === employeeName);
      return {
        ...item,
        eventData: {
          ...item.eventData,
          nextEmployee: newEmployee,
          prevEmployee: {
            employeeCode: item.employeeCode,
            employeeName: item.group,
          },
        },
      };
    }
    return item;
  };

  const handleCanvasClick = (group, time) => {
    const hour = moment(time).hour();
    const start = moment(time).startOf('day').add(hour, 'hour');
    const code = groups.find((g) => g.id === group).code;

    const addItem = {start, code, end: ''};

    handleSelectSlot(addItem);
  };

  const handleItemDrag = ({eventType, itemId, time, edge, newGroupOrder}) => {
    const item = items.find((item) => item.id === itemId);
    setDragItem({
      item,
      time,
    });
  };

  const handleItemDoubleClick = (itemId) => {
    const item = items.find(({id}) => id === itemId);
    onUpdateDispatch(JSON.parse(JSON.stringify(item)));
  };

  return {
    summaryEvents,
    setSummaryEvents,
    currentView,
    setCurrentView,
    events,
    setEvents,
    groups,
    setGroups,
    items,
    setItems,
    dragItem,
    setDragItem,
    generateFormattedEvent,
    generateSummaryEvents,
    handleItemResize,
    handleItemMove,
    handleCanvasClick,
    handleItemDrag,
    handleItemDoubleClick,
  };
}
